import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Component, component, field, ObjectManager, property } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { EditorSession } from '../../canvas/EditorSession';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Physical, PotentialInteraction, RelativeTo } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Snappable } from '../../canvas/entities/Snappable';
import { EffectDirector, Effects } from '../../canvas/visualization/EffectDirector';
import { Pingable } from '../presentation/Pingable';
import { cardPileDrawAll, cardPileDrawOne, cardPileShuffle } from './CardPileCommands';
import { CardPileLabel } from './CardPileLabel';
import { CardPileLayout } from './CardPileLayout';
import { ValueCard } from './ValueCard';

const invocation = defineCommand(
    'cardPileInvocation',
    null,
    (cc, [id, hint]: [string, string?]) => {
        if (hint === 'shuffle') {
            cc.dispatch(cardPileShuffle, [id]);
        }
        else if (hint === 'drawOne') {
            cc.dispatch(cardPileDrawOne, [id]);
        }
        else if (hint === 'drawAll') {
            cc.dispatch(cardPileDrawAll, [id]);
        }
    },
    [],
);

const mutation = defineCommand(
    'cardPileMutation',
    null,
    (cc, [id]: [string]) => {
        cc.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', 'label'));
        // const objects = r.call(ObjectManager);
        // const pack = objects.get(id, CardPile);
        // const dispatch = r.call(CommandDispatcher);
        // if (pack.param) {
        //     dispatch(handDeal, [pack.id]);
        // }
    },
    [],
);

const drop = defineCommand(
    'cardPileDrop',
    null,
    (r, [id, targetIds]: [string, string[]]) => {

        const objects = r.call(ObjectManager);
        const effects = r.call(EffectDirector);

        const pile = objects.get(id, CardPile);
        const subjects = resolveSubjects(r, targetIds);

        if (!subjects.length) return;

        objects.update(() => {
            const newPos = pile.layout.stack.topLeft().add(pile.position(RelativeTo.Model));
            for (const s of subjects) {
                const from = s.transform(RelativeTo.Model);
                s.moveTo(newPos);
                const to = s.transform(RelativeTo.Model);
                effects.trigger(Effects.tween(from, to), s);

                const vc = s.as(ValueCard);
                if (pile.face === 'up' && vc.concealed) {
                    vc.concealed = false;
                }
                else if (pile.face === 'down' && !vc.concealed) {
                    vc.concealed = true;
                }
            }
        });
    }
);

const cardPileDef = {
    name: 'CardPile',
    implements: [Physical, Snappable, Pingable, DropTarget],
    // propertyLayout: cardPilePropertyLayout,
    invocation,
    mutation,
};

const cardPileTransformHints = Object.freeze({
    scaleX: true,
    scaleY: true,
    rotate: false,
    translate: true,
    visible: true,
});

@component(cardPileDef)
export class CardPile extends RectangularPhysical implements DropTarget {

    @property({ type: 'number', default: 140 })
    public width!: number;

    @property({ type: 'number', default: 180 })
    public height!: number;

    @property({ type: 'string', default: 'any' })
    public face: 'any' | 'up' | 'down';

    @field({ type: CardPileLabel, valueOnly: true })
    public label: string;

    public get layout() {
        return CardPileLayout(this);
    }

    public get shouldSnap() {
        return true;
    }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {
        if (!this.localBounds.contains(this.toRelative(mpt))) return null;
        for (const s of subjects) {
            if (!s.is(ValueCard)) return null;
        }
        return {
            target: this,
            guides: [{
                points: this.transform(RelativeTo.Model).apply(this.layout.stack),
            }],
            command: drop,
        };
    }

    public queryTransformHints() {
        return cardPileTransformHints;
    }

    public bounds(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds.inflate([0, 0]).offset([0, -0])));
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { label, shuffle, drawOne, drawAll } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (type === 'invoke' && shuffle.contains(rpt)) {
                return [true, 'shuffle'];
            }
            if (type === 'invoke' && drawOne.contains(rpt)) {
                return [true, 'drawOne'];
            }
            if (type === 'invoke' && drawAll.contains(rpt)) {
                return [true, 'drawAll'];
            }
            if (type !== 'invoke') {
                return [true, 'default'];
            }
        }

        if (this.localBounds.contains(rpt)) {
            // if (type === 'mutate') {
            //     if (label.contains(rpt)) return [true, 'label'];
            // }
            // else {
            if (type !== 'invoke') {
                return [true, 'default'];
            }
            // }
        }
        return [false, 'none'];
    }
}

